import { useNavigate, NavigateFunction } from 'react-router';
import { useState, useRef } from 'react';
import { useBankIDAuthInitMutation, useClientQuery } from 'hooks/webapi';
import useRedirect from 'hooks/webapi/useRedirect';
import { BANK_ID_ERROR, MANUAL_OCR } from 'Router/routes';
import {
  BankIdErrorProps,
  BankIdErrorPropsMap,
  getErrorValueByKey,
  getKeyByErrorValue,
} from 'pages/Registration/BankId/BankError/errorTranslation';
import { Profile } from 'hooks/webapi/bankID/useBankIDSessionsQuery';
import { getIsStageOrProdEnv } from 'utils/getEnv';

// Persists the error code to sessionStorage so the BankError page can read it after navigation
export const handleErrorRedirect = (
  navigate: NavigateFunction,
  reason?: BankIdErrorProps | string,
  skipFormat = false,
) => {
  // Persist the error status so BankError page can read it after navigation
  sessionStorage.setItem(
    'sessionStatus',
    skipFormat
      ? (reason ?? '')
      : getErrorValueByKey(
          (Number(reason) || 1) as keyof typeof BankIdErrorPropsMap,
        ),
  );

  navigate(BANK_ID_ERROR);
};

export default function useBankIdInfo(enableSessionStatusModal?: boolean) {
  const navigate = useNavigate();
  const redirect = useRedirect();

  const [isLoadingOverride, setIsLoadingOverride] = useState(false);

  const [isSessionStatusModalOpen, setIsSessionStatusModalOpen] =
    useState(false);
  const statusPromiseRef = useRef<Promise<Profile['sessionStatus']> | null>(
    null,
  );
  const resolveStatusRef = useRef<
    ((status: Profile['sessionStatus']) => void) | null
  >(null);

  const {
    mutateAsync: initBankIdSession,
    isPending: initBankIdSessionIsPending,
  } = useBankIDAuthInitMutation();

  const { refetch: refetchClient } = useClientQuery();

  const initBankId = async () => {
    setIsLoadingOverride(true);

    try {
      const { data: client } = await refetchClient();

      // If client is not loaded, we can't proceed
      if (!client) {
        handleErrorRedirect(navigate, 'client_not_loaded');
        setIsLoadingOverride(false);
        return;
      }

      const redirectURL = await initBankIdSession({
        product: import.meta.env.VITE_PSD2_PRODUCT_NAME,
        clientNumber: client?.number || '',
      });

      // If redirectURL is not loaded, we can't proceed
      if (!redirectURL) {
        handleErrorRedirect(navigate, 'redirect_url_not_loaded');
        return;
      }

      const sessionId = new URLSearchParams(redirectURL.url).get('state');

      // If sessionId is not loaded, we can't proceed
      if (!sessionId) {
        handleErrorRedirect(navigate, 'session_id_not_loaded');
        return;
      }

      localStorage.setItem('bankIdSessionId', sessionId);

      // Open modal to select session status only for TEST
      if (!getIsStageOrProdEnv() && enableSessionStatusModal) {
        // Deferred-promise pattern: resolveStatusRef is called by the modal's onSelect to unblock this await
        statusPromiseRef.current = new Promise((resolve) => {
          resolveStatusRef.current = resolve;
        });

        setIsSessionStatusModalOpen(true);

        const selectedStatus = await statusPromiseRef.current;

        // save to handle on BankError screen
        if (selectedStatus) {
          const statusKey = getKeyByErrorValue(selectedStatus);
          if (statusKey) {
            sessionStorage.setItem('sessionStatus', statusKey);
          }
        }
      }

      redirect(redirectURL.url);
    } catch (e) {
      console.error(e);
      // TODO: Handle error
    } finally {
      // As the actual redirection takes some time, we need to override the loading state for a while to prevent double initiation
      setTimeout(() => {
        setIsLoadingOverride(false);
      }, 10 * 1000);
    }
  };

  const handleSkip = () => {
    navigate(MANUAL_OCR);
  };

  return {
    initBankId,
    handleSkip,
    isPending: initBankIdSessionIsPending || isLoadingOverride,
    sessionStatusModalProps: {
      isOpen: isSessionStatusModalOpen,
      onClose: () => undefined,
      onSelect: (status: NonNullable<Profile['sessionStatus']>) => {
        if (resolveStatusRef.current) {
          resolveStatusRef.current(status);
        }
        setIsSessionStatusModalOpen(false);
      },
    },
  };
}
